// Safe Wallet Beta 1 recovery cryptography.
//
// Recovery uses two independent 32-byte shares:
//   recoverySecret = serverShare XOR custodianShare
// The server stores only serverShare and an AEAD envelope. The same custodianShare
// may be sealed independently to several trusted users, so any one verified contact
// can help while the server alone still cannot recover the vault.

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const ECDH_PARAMS: EcKeyGenParams = { name: 'ECDH', namedCurve: 'P-256' };
const ECIES_INFO = encoder.encode('safe-wallet:recovery-ecies:v2');
const ENVELOPE_AAD = encoder.encode('safe-wallet:recovery-envelope:v2');
const DRAFT_AAD_PREFIX = 'safe-wallet:recovery-draft:v2:';
const CONTACT_AAD_PREFIX = 'safe-wallet:recovery-contact:v2:';
const CUSTODIAN_KEY_AAD_PREFIX = 'safe-wallet:recovery-custodian-private-key:v2:';
const APPROVAL_AAD_PREFIX = 'safe-wallet:recovery-approval:v2:';
const FINGERPRINT_CONTEXT = encoder.encode('safe-wallet:recovery-key-fingerprint:v2');
const FINGERPRINT_BYTES = 10;
const SECRET_BYTES = 32;

function subtle(): SubtleCrypto {
  if (!globalThis.crypto?.subtle) throw new Error('This environment does not provide WebCrypto.');
  return globalThis.crypto.subtle;
}

function bytes(value: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(value.byteLength);
  copy.set(value);
  return copy;
}

export function recoveryToBase64(value: Uint8Array): string {
  let binary = '';
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function recoveryFromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const result = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) result[index] = binary.charCodeAt(index);
  return result;
}

function xor(left: Uint8Array, right: Uint8Array): Uint8Array {
  if (left.length !== right.length) throw new Error('Recovery shares have mismatched length.');
  const result = new Uint8Array(left.length);
  for (let index = 0; index < left.length; index += 1) result[index] = left[index] ^ right[index];
  return result;
}

async function sha256(value: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest('SHA-256', bytes(value)));
}

export async function recoveryHashHex(value: Uint8Array): Promise<string> {
  const digest = await sha256(value);
  return Array.from(digest, (part) => part.toString(16).padStart(2, '0')).join('');
}

export interface RecoveryEnvelope {
  version: 2;
  algorithm: 'AES-256-GCM';
  iv: string;
  ciphertext: string;
}

export interface SealedRecoveryValue {
  version: 2;
  algorithm: 'P-256-HKDF-SHA256-AES256GCM';
  ephemeralPublicKey: string;
  iv: string;
  ciphertext: string;
}

export interface LocalRecoveryDraft {
  version: 2;
  iv: string;
  ciphertext: string;
}

export interface VaultProtectedRecoveryKey {
  version: 1;
  algorithm: 'AES-256-GCM';
  iv: string;
  ciphertext: string;
}

export interface RecoveryApprovalContext {
  requestId: string;
  recoverySetId: string;
  vaultId: string;
  ownerUserId: string;
  custodianUserId: string;
  custodianId: string;
  recoveringPublicKey: string;
  recoverySetVersion: number;
  expiresAt: string;
}

interface RecoveryEnvelopePayload {
  version: 2;
  vaultRootKey: string;
  completionSecret: string;
}

interface ContactPackagePayload {
  version: 2;
  recoverySetId: string;
  custodianShare: string;
  activationChallenge: string;
}

export interface RecoveryDraftProvisioning {
  envelope: RecoveryEnvelope;
  serverShareBase64: string;
  completionSecretHash: string;
  encryptedLocalDraft: LocalRecoveryDraft;
}

export interface RecoveryContactProvisioning {
  sealedPackage: SealedRecoveryValue;
  activationChallengeHash: string;
  fingerprint: string;
}

export interface RecoveredVaultMaterial {
  vaultRootKey: Uint8Array;
  completionSecretBase64: string;
}

export function serializeRecoveryApprovalContext(context: RecoveryApprovalContext): Uint8Array {
  const fields = [
    context.requestId,
    context.recoverySetId,
    context.vaultId,
    context.ownerUserId,
    context.custodianUserId,
    context.custodianId,
    context.recoveringPublicKey,
    String(context.recoverySetVersion),
    context.expiresAt,
  ];
  return encoder.encode(`${APPROVAL_AAD_PREFIX}${fields.join('\u241f')}`);
}

async function importPublicKey(spkiBase64: string): Promise<CryptoKey> {
  const raw = recoveryFromBase64(spkiBase64);
  if (raw.byteLength < 40 || raw.byteLength > 512) throw new Error('Invalid recovery public key.');
  return subtle().importKey('spki', raw, ECDH_PARAMS, false, []);
}

async function deriveEciesKey(sharedBits: Uint8Array, salt: Uint8Array): Promise<CryptoKey> {
  const base = await subtle().importKey('raw', bytes(sharedBits), 'HKDF', false, ['deriveBits']);
  const derived = await subtle().deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: bytes(salt), info: bytes(ECIES_INFO) },
    base,
    256,
  );
  return subtle().importKey('raw', derived, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

async function sealToPublicKey(
  publicKey: string,
  plaintext: Uint8Array,
  associatedData: Uint8Array,
): Promise<SealedRecoveryValue> {
  const recipient = await importPublicKey(publicKey);
  const ephemeral = await subtle().generateKey(ECDH_PARAMS, true, ['deriveBits']);
  const ephemeralPublicKey = new Uint8Array(await subtle().exportKey('spki', ephemeral.publicKey));
  const sharedBits = new Uint8Array(
    await subtle().deriveBits({ name: 'ECDH', public: recipient }, ephemeral.privateKey, 256),
  );
  try {
    const key = await deriveEciesKey(sharedBits, ephemeralPublicKey);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = new Uint8Array(
      await subtle().encrypt(
        { name: 'AES-GCM', iv: bytes(iv), additionalData: bytes(associatedData) },
        key,
        bytes(plaintext),
      ),
    );
    return {
      version: 2,
      algorithm: 'P-256-HKDF-SHA256-AES256GCM',
      ephemeralPublicKey: recoveryToBase64(ephemeralPublicKey),
      iv: recoveryToBase64(iv),
      ciphertext: recoveryToBase64(ciphertext),
    };
  } finally {
    sharedBits.fill(0);
  }
}

async function openWithPrivateKey(
  privateKey: CryptoKey,
  sealed: SealedRecoveryValue,
  associatedData: Uint8Array,
): Promise<Uint8Array> {
  if (sealed.version !== 2 || sealed.algorithm !== 'P-256-HKDF-SHA256-AES256GCM') {
    throw new Error('Unsupported recovery package.');
  }
  const ephemeral = await importPublicKey(sealed.ephemeralPublicKey);
  const sharedBits = new Uint8Array(
    await subtle().deriveBits({ name: 'ECDH', public: ephemeral }, privateKey, 256),
  );
  try {
    const key = await deriveEciesKey(sharedBits, recoveryFromBase64(sealed.ephemeralPublicKey));
    const plaintext = await subtle().decrypt(
      {
        name: 'AES-GCM',
        iv: recoveryFromBase64(sealed.iv),
        additionalData: bytes(associatedData),
      },
      key,
      recoveryFromBase64(sealed.ciphertext),
    );
    return new Uint8Array(plaintext);
  } finally {
    sharedBits.fill(0);
  }
}

async function importAesKey(raw: Uint8Array, usages: KeyUsage[]): Promise<CryptoKey> {
  if (raw.byteLength !== SECRET_BYTES) throw new Error('Invalid recovery key length.');
  return subtle().importKey('raw', bytes(raw), { name: 'AES-GCM', length: 256 }, false, usages);
}

async function encryptAesGcm(rawKey: Uint8Array, plaintext: Uint8Array, aad: Uint8Array): Promise<{ iv: string; ciphertext: string }> {
  const key = await importAesKey(rawKey, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await subtle().encrypt(
      { name: 'AES-GCM', iv: bytes(iv), additionalData: bytes(aad) },
      key,
      bytes(plaintext),
    ),
  );
  return { iv: recoveryToBase64(iv), ciphertext: recoveryToBase64(ciphertext) };
}

async function decryptAesGcm(rawKey: Uint8Array, iv: string, ciphertext: string, aad: Uint8Array): Promise<Uint8Array> {
  const key = await importAesKey(rawKey, ['decrypt']);
  return new Uint8Array(
    await subtle().decrypt(
      { name: 'AES-GCM', iv: recoveryFromBase64(iv), additionalData: bytes(aad) },
      key,
      recoveryFromBase64(ciphertext),
    ),
  );
}

function custodianKeyAad(userId: string, publicKey: string): Uint8Array {
  if (!userId.trim()) throw new Error('Recovery helper user is required.');
  return encoder.encode(`${CUSTODIAN_KEY_AAD_PREFIX}${userId}:${publicKey}`);
}

export async function protectRecoveryCustodianPrivateKey(params: {
  vaultRootKey: Uint8Array;
  userId: string;
  publicKey: string;
  privateKeyPkcs8: Uint8Array;
}): Promise<VaultProtectedRecoveryKey> {
  await importPublicKey(params.publicKey);
  if (params.privateKeyPkcs8.byteLength < 80 || params.privateKeyPkcs8.byteLength > 1024) {
    throw new Error('Invalid recovery helper private key.');
  }
  const protectedKey = await encryptAesGcm(
    params.vaultRootKey,
    params.privateKeyPkcs8,
    custodianKeyAad(params.userId, params.publicKey),
  );
  return { version: 1, algorithm: 'AES-256-GCM', ...protectedKey };
}

export async function openRecoveryCustodianPrivateKey(params: {
  vaultRootKey: Uint8Array;
  userId: string;
  publicKey: string;
  protectedPrivateKey: VaultProtectedRecoveryKey;
}): Promise<Uint8Array> {
  await importPublicKey(params.publicKey);
  if (params.protectedPrivateKey.version !== 1 || params.protectedPrivateKey.algorithm !== 'AES-256-GCM') {
    throw new Error('Unsupported recovery helper key.');
  }
  return decryptAesGcm(
    params.vaultRootKey,
    params.protectedPrivateKey.iv,
    params.protectedPrivateKey.ciphertext,
    custodianKeyAad(params.userId, params.publicKey),
  );
}

export async function recoveryKeyFingerprint(publicKey: string): Promise<string> {
  const raw = recoveryFromBase64(publicKey);
  await importPublicKey(publicKey);
  const material = new Uint8Array(FINGERPRINT_CONTEXT.length + raw.length);
  material.set(FINGERPRINT_CONTEXT, 0);
  material.set(raw, FINGERPRINT_CONTEXT.length);
  const digest = (await sha256(material)).subarray(0, FINGERPRINT_BYTES);
  const hex = Array.from(digest, (part) => part.toString(16).padStart(2, '0')).join('').toUpperCase();
  return hex.replace(/(.{4})(?=.)/g, '$1 ');
}

export interface RecoveryRecipient {
  publicKey: string;
  privateKey: CryptoKey;
}

export async function createRecoveryRecipient(): Promise<RecoveryRecipient> {
  const keyPair = await subtle().generateKey(ECDH_PARAMS, false, ['deriveBits']);
  return {
    publicKey: recoveryToBase64(new Uint8Array(await subtle().exportKey('spki', keyPair.publicKey))),
    privateKey: keyPair.privateKey,
  };
}

export interface ExportableRecoveryCustodian {
  publicKey: string;
  privateKeyPkcs8: Uint8Array;
}

export async function createExportableRecoveryCustodian(): Promise<ExportableRecoveryCustodian> {
  const keyPair = await subtle().generateKey(ECDH_PARAMS, true, ['deriveBits']);
  return {
    publicKey: recoveryToBase64(new Uint8Array(await subtle().exportKey('spki', keyPair.publicKey))),
    privateKeyPkcs8: new Uint8Array(await subtle().exportKey('pkcs8', keyPair.privateKey)),
  };
}

export async function importRecoveryCustodian(publicKey: string, privateKeyPkcs8: Uint8Array): Promise<SoftwareRecoveryCustodian> {
  await importPublicKey(publicKey);
  const privateKey = await subtle().importKey(
    'pkcs8',
    bytes(privateKeyPkcs8),
    ECDH_PARAMS,
    false,
    ['deriveBits'],
  );
  return SoftwareRecoveryCustodian.fromStored(publicKey, privateKey);
}

export class SoftwareRecoveryCustodian {
  readonly publicKey: string;
  private readonly privateKey: CryptoKey;

  private constructor(publicKey: string, privateKey: CryptoKey) {
    this.publicKey = publicKey;
    this.privateKey = privateKey;
  }

  static async create(): Promise<SoftwareRecoveryCustodian> {
    const keyPair = await subtle().generateKey(ECDH_PARAMS, false, ['deriveBits']);
    const publicKey = recoveryToBase64(new Uint8Array(await subtle().exportKey('spki', keyPair.publicKey)));
    return new SoftwareRecoveryCustodian(publicKey, keyPair.privateKey);
  }

  static fromStored(publicKey: string, privateKey: CryptoKey): SoftwareRecoveryCustodian {
    return new SoftwareRecoveryCustodian(publicKey, privateKey);
  }

  persistablePrivateKey(): CryptoKey {
    return this.privateKey;
  }

  async openContactPackage(recoverySetId: string, sealedPackage: SealedRecoveryValue): Promise<ContactPackagePayload> {
    const plaintext = await openWithPrivateKey(
      this.privateKey,
      sealedPackage,
      encoder.encode(`${CONTACT_AAD_PREFIX}${recoverySetId}`),
    );
    try {
      const parsed = JSON.parse(decoder.decode(plaintext)) as ContactPackagePayload;
      if (parsed.version !== 2 || parsed.recoverySetId !== recoverySetId) throw new Error('Recovery package does not match this setup.');
      if (recoveryFromBase64(parsed.custodianShare).byteLength !== SECRET_BYTES) throw new Error('Invalid recovery share.');
      return parsed;
    } finally {
      plaintext.fill(0);
    }
  }

  async approveAndReseal(
    recoverySetId: string,
    sealedPackage: SealedRecoveryValue,
    recoveringPublicKey: string,
    context: RecoveryApprovalContext,
  ): Promise<SealedRecoveryValue> {
    const payload = await this.openContactPackage(recoverySetId, sealedPackage);
    const share = recoveryFromBase64(payload.custodianShare);
    try {
      return await sealToPublicKey(recoveringPublicKey, share, serializeRecoveryApprovalContext(context));
    } finally {
      share.fill(0);
    }
  }
}

export async function createRecoveryDraftProvisioning(
  vaultRootKey: Uint8Array,
  recoverySetId: string,
): Promise<RecoveryDraftProvisioning> {
  if (vaultRootKey.byteLength !== SECRET_BYTES) throw new Error('Invalid Vault Root Key.');
  const recoverySecret = crypto.getRandomValues(new Uint8Array(SECRET_BYTES));
  const serverShare = crypto.getRandomValues(new Uint8Array(SECRET_BYTES));
  const custodianShare = xor(recoverySecret, serverShare);
  const completionSecret = crypto.getRandomValues(new Uint8Array(SECRET_BYTES));
  try {
    const payload: RecoveryEnvelopePayload = {
      version: 2,
      vaultRootKey: recoveryToBase64(vaultRootKey),
      completionSecret: recoveryToBase64(completionSecret),
    };
    const wrapped = await encryptAesGcm(recoverySecret, encoder.encode(JSON.stringify(payload)), ENVELOPE_AAD);
    const localWrapped = await encryptAesGcm(
      vaultRootKey,
      custodianShare,
      encoder.encode(`${DRAFT_AAD_PREFIX}${recoverySetId}`),
    );
    return {
      envelope: { version: 2, algorithm: 'AES-256-GCM', ...wrapped },
      serverShareBase64: recoveryToBase64(serverShare),
      completionSecretHash: await recoveryHashHex(completionSecret),
      encryptedLocalDraft: { version: 2, ...localWrapped },
    };
  } finally {
    recoverySecret.fill(0);
    serverShare.fill(0);
    custodianShare.fill(0);
    completionSecret.fill(0);
  }
}

export async function openLocalRecoveryDraft(
  vaultRootKey: Uint8Array,
  recoverySetId: string,
  draft: LocalRecoveryDraft,
): Promise<Uint8Array> {
  if (draft.version !== 2) throw new Error('Unsupported local recovery draft.');
  return decryptAesGcm(
    vaultRootKey,
    draft.iv,
    draft.ciphertext,
    encoder.encode(`${DRAFT_AAD_PREFIX}${recoverySetId}`),
  );
}

export async function createRecoveryContactProvisioning(params: {
  recoverySetId: string;
  custodianShare: Uint8Array;
  custodianPublicKey: string;
}): Promise<RecoveryContactProvisioning> {
  if (params.custodianShare.byteLength !== SECRET_BYTES) throw new Error('Invalid recovery share.');
  const activationChallenge = crypto.getRandomValues(new Uint8Array(SECRET_BYTES));
  try {
    const payload: ContactPackagePayload = {
      version: 2,
      recoverySetId: params.recoverySetId,
      custodianShare: recoveryToBase64(params.custodianShare),
      activationChallenge: recoveryToBase64(activationChallenge),
    };
    return {
      sealedPackage: await sealToPublicKey(
        params.custodianPublicKey,
        encoder.encode(JSON.stringify(payload)),
        encoder.encode(`${CONTACT_AAD_PREFIX}${params.recoverySetId}`),
      ),
      activationChallengeHash: await recoveryHashHex(activationChallenge),
      fingerprint: await recoveryKeyFingerprint(params.custodianPublicKey),
    };
  } finally {
    activationChallenge.fill(0);
  }
}

export async function verifyRecoveryContactPackage(params: {
  custodian: SoftwareRecoveryCustodian;
  recoverySetId: string;
  sealedPackage: SealedRecoveryValue;
}): Promise<string> {
  const payload = await params.custodian.openContactPackage(params.recoverySetId, params.sealedPackage);
  const challenge = recoveryFromBase64(payload.activationChallenge);
  try {
    return recoveryToBase64(challenge);
  } finally {
    challenge.fill(0);
  }
}

export async function recoverVaultMaterial(params: {
  envelope: RecoveryEnvelope;
  serverShareBase64: string;
  resealedShare: SealedRecoveryValue;
  recipient: RecoveryRecipient;
  context: RecoveryApprovalContext;
}): Promise<RecoveredVaultMaterial> {
  if (params.envelope.version !== 2 || params.envelope.algorithm !== 'AES-256-GCM') {
    throw new Error('Unsupported recovery envelope.');
  }
  const custodianShare = await openWithPrivateKey(
    params.recipient.privateKey,
    params.resealedShare,
    serializeRecoveryApprovalContext(params.context),
  );
  const serverShare = recoveryFromBase64(params.serverShareBase64);
  const recoverySecret = xor(serverShare, custodianShare);
  try {
    const plaintext = await decryptAesGcm(
      recoverySecret,
      params.envelope.iv,
      params.envelope.ciphertext,
      ENVELOPE_AAD,
    );
    try {
      const payload = JSON.parse(decoder.decode(plaintext)) as RecoveryEnvelopePayload;
      if (payload.version !== 2) throw new Error('Unsupported recovered vault material.');
      const vaultRootKey = recoveryFromBase64(payload.vaultRootKey);
      if (vaultRootKey.byteLength !== SECRET_BYTES) throw new Error('Recovered Vault Root Key is invalid.');
      if (recoveryFromBase64(payload.completionSecret).byteLength !== SECRET_BYTES) {
        vaultRootKey.fill(0);
        throw new Error('Recovery completion secret is invalid.');
      }
      return { vaultRootKey, completionSecretBase64: payload.completionSecret };
    } finally {
      plaintext.fill(0);
    }
  } finally {
    custodianShare.fill(0);
    serverShare.fill(0);
    recoverySecret.fill(0);
  }
}
